import type { InternalAxiosRequestConfig } from 'axios';
import axios, { AxiosError } from 'axios';
import { isString } from 'lodash-es';
import { snackbarApi } from '@/services/snackbar';
import { extractErrorsIfBadRequest } from '@/features/pipelines/destinations';
import { getCurrentApiUrl } from '@/utils/environment';
import { sentryCaptureException } from '@/utils/sentry';
import { getAppVersion } from '@/utils/version';
import {
  ACTIVE_ACCOUNT_HEADER,
  ACTIVE_SESSION_HEADER,
  activeAccountIdStore,
  activeSessionIdStore,
} from '@customerio/hydra-session-state';

export const api = axios.create({
  baseURL: '/cdp/api',
  withCredentials: true,
});

const computeBaseUrlInterceptor = async (config: InternalAxiosRequestConfig<unknown>) => {
  config.baseURL = getCurrentApiUrl();
  return config;
};

const getAppVersionInterceptor = async (config: InternalAxiosRequestConfig<unknown>) => {
  config.headers['x-ui-version'] = getAppVersion();
  return config;
};

const setSessionIndexInterceptor = async (config: InternalAxiosRequestConfig<unknown>) => {
  config.headers[ACTIVE_SESSION_HEADER] = activeSessionIdStore.get().toString();

  return config;
};

const setActiveAccountInterceptor = async (config: InternalAxiosRequestConfig<unknown>) => {
  // Only attach a positive account selection. 0 (home) is equivalent to omitting
  // it and avoids putting a CORS-preflighted header on every Pipelines request
  // for users who never switch accounts. Matches getAuthHeaders + auth bootstrap.
  const activeAccountId = activeAccountIdStore.get();
  if (activeAccountId > 0) {
    config.headers[ACTIVE_ACCOUNT_HEADER] = activeAccountId.toString();
  }

  return config;
};

api.interceptors.request.use(async (config) => {
  config = await computeBaseUrlInterceptor(config);
  config = await getAppVersionInterceptor(config);
  config = await setSessionIndexInterceptor(config);
  config = await setActiveAccountInterceptor(config);
  return config;
});

// oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
export function notifyOnError(error: any) {
  let message = '';

  if (error instanceof AxiosError) {
    const extracted = extractErrorsIfBadRequest(error).join('\n');
    message = extracted !== '' ? extracted : error.message;
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = isString(error) ? error : JSON.stringify(error);
  }

  snackbarApi.create({ variant: 'critical', message });

  sentryCaptureException(error);
}
